import { startCase } from 'lodash';
import { ResponseBody } from '@opensearch-project/opensearch/api/_types/_core.search';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInHours,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  getDate,
  getDay,
  getMonth,
  isAfter,
  isBefore,
  setDate,
  setDay,
  setMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';

import { PLACEHOLDER } from '@/constants';
import { IStoreMetadata } from '@/data';
import {
  Asset,
  CannabisType,
  Discount,
  GProduct,
  LabResult,
  OpenSearchDiscountCondition,
  OpenSearchProduct,
  OpenSearchProductDiscount,
  OpenSearchProductLabResult,
  OpenSearchProductPricing,
  OpenSearchScheduleCondition,
  OpenSearchTierDetail,
  PriceType,
  Pricing,
  Store,
  TierMethod,
  Variant,
} from '@/types';

import {
  calculatePrice,
  formatProductTitle,
  getLabValue,
  getSalePrice,
  getStockItem,
  PRODUCT_URL_PATH,
} from './treez';

import data from '@/public/ssr-cache.json';

const store = data?.store as Store;
const timezone = store?.timezone ?? 'US/Pacific';

const mapProduct = (product: OpenSearchProduct): GProduct => {
  const assets = mapPhotoAssets(product);
  const trezPriceType = priceType(product);

  const inStock = (product?.availableUnits ?? 0) > 0;
  const outStock = (product?.availableUnits ?? 0) === 0;

  /**
   * =====================================================================================
   * DISCOUNT CALCULATION AND PRICING LOGIC
   * =====================================================================================
   *
   * This section handles the complex discount calculation logic for cannabis products.
   * It processes multiple types of discounts and applies them to determine the final
   * discounted price, amount, and percentage for each product.
   *
   * PROCESS OVERVIEW:
   * 1. Filter and map discounts that are currently active based on schedule conditions
   * 2. Calculate the minimum possible discounted price from all applicable pre-cart discounts
   * 3. Compare with existing discounted price and take the better deal for the customer
   * 4. Update the product's pricing data with calculated discount information
   *
   * DISCOUNT TYPES HANDLED:
   * - PERCENT: Percentage-based discounts (e.g., 20% off)
   * - DOLLAR: Fixed dollar amount discounts (e.g., $5 off)
   * - TARGET_PRICE: Fixed target price discounts (e.g., $10 PRICE)
   *
   * DISCOUNT AFFINITY:
   * - 'Pre-Cart': Discounts applied before items are added to cart (automatic)
   * - 'Cart': Discounts applied during checkout process (may require codes/conditions)
   *
   * NOTE: Only 'Pre-Cart' discounts are processed here as they represent automatic
   * discounts that should be immediately visible to customers browsing products.
   * =====================================================================================
   */
  const mappedDiscounts = mapDiscounts(
    filterDiscountsOutOfSchedule(product?.productData?.discounts),
  );

  // Calculate base price using tier pricing (first tier value) or flat pricing (sell price)
  const isTierType = product?.productData?.pricing?.priceType === 'TIER';
  const firstTierValue =
    product?.productData?.pricing?.tierDetails?.[0].pricePerValue ?? 0;
  const priceSellValue = product?.productData?.pricing?.priceSell ?? 0;

  const priceSell = isTierType ? firstTierValue : priceSellValue;

  // Track the minimum discounted price across all applicable discounts
  let minDiscountedPrice = priceSell;
  let stackableDiscountsSum = 0;
  // Track IDs of stackable discounts to know which ones were combined
  const stackableDiscountIds: string[] = [];
  // Track the best non-stackable discount (ID + price) to compare against stackable sum
  let bestNonStackable: { id: string; price: number } | null = null;

  mappedDiscounts.forEach(discount => {
    if (discount.discount_affinity === 'Pre-Cart') {
      const isStackable = discount.discount_stackable === 'Yes';
      let discountAmount = 0;

      if (discount.discount_method === 'PERCENT') {
        const percent = discount.discount_amount ?? 0;
        discountAmount = (percent / 100) * priceSell;
      } else if (discount.discount_method === 'DOLLAR') {
        discountAmount = discount.discount_amount ?? 0;
      } else if (discount.discount_method === 'TARGET_PRICE') {
        discountAmount = discount.discount_amount
          ? priceSell - discount.discount_amount
          : 0;
      }

      const discountedPrice = priceSell - discountAmount;
      minDiscountedPrice = Math.min(minDiscountedPrice, discountedPrice);

      if (isStackable) {
        stackableDiscountsSum += discountAmount;
        stackableDiscountIds.push(discount.discount_id);
      } else if (
        !bestNonStackable ||
        discountedPrice < bestNonStackable.price
      ) {
        bestNonStackable = { id: discount.discount_id, price: discountedPrice };
      }
    }
  });

  const stackableDiscountedPrice = priceSell - stackableDiscountsSum;
  minDiscountedPrice = Math.min(minDiscountedPrice, stackableDiscountedPrice);

  // Determine which discount IDs to mark as applied:
  // - If best non-stackable gives better price than all stackables combined, use only that ID
  // - Otherwise, use all stackable discount IDs
  const appliedDiscountIds: string[] =
    bestNonStackable &&
    (bestNonStackable as any)?.price < stackableDiscountedPrice
      ? [(bestNonStackable as any)?.id]
      : stackableDiscountIds;

  // Apply the best available discount and update product pricing data
  if (product.productData?.pricing) {
    const discountedPrice = minDiscountedPrice;
    const discountAmount = priceSell - discountedPrice;
    const discountPercent = (discountAmount / priceSell) * 100;

    product.productData.pricing.discountedPrice = discountedPrice;
    product.productData.pricing.discountAmount = discountAmount;
    product.productData.pricing.discountPercent = discountPercent;
    // Store applied discount IDs so UI components know which discounts actually affect this product
    product.productData.pricing.appliedDiscountIds = appliedDiscountIds;
  }

  return {
    id: product.id,
    name: product?.menuTitle || formatProductTitle(product.name ?? ''),
    url: `${PRODUCT_URL_PATH}${product.slug}`,
    slug: product?.oldSlug ?? '', // TODO: replace with product.old_slug when available
    path_slug: product.slug ?? '',
    enabled: product.isActive,
    description: product.description,
    category: startCase(product.category?.toLocaleLowerCase()),
    subcategory: product.subtype,
    category_id: product.category,

    thc: getLabValue(product.thc),
    cbd: getLabValue(product.cbd),

    qty: product?.availableUnits,
    brand_name: product.brand,
    brand_id: product.brand,
    brand_subtype: startCase(product.name?.toLocaleLowerCase()),
    flavors: product.flavors,
    effects: product.effects,
    ingridients: product.ingredients,
    in_stock: inStock,
    sales_price: product.productData?.pricing?.priceSell ?? 0 * 100,
    flower_type: startCase(product.classification?.toLocaleLowerCase()),
    cannabis_type: product.classification as CannabisType,
    available_for_delivery: true, // not available in sell treez at the moment
    available_for_pickup: true, // not available in sell treez at the moment
    asset: assets[0],
    assets: assets,
    price_type: trezPriceType as PriceType,
    variants: mapVariants(product),
    discounts: mappedDiscounts,
    labResults: mapLabResults(product?.productData?.labResults),
    isOutStock: outStock,
    isLowStock: !product.isAboveThreshold,
    base_weight: product?.amount,
    tier_method: product?.productData?.pricing?.tierMethod as TierMethod,
    pricing: mapPricing(product?.productData?.pricing),

    isHideFromMenu: product?.isHideFromMenu,
  };
};

const mapPricing = (pricing?: OpenSearchProductPricing): Pricing => {
  if (!pricing) {
    return {
      price_type: 'FLAT',
      tier_method: 'FLAT',
      tier_name: '',
      discount_amount: 0,
      discount_percent: 0,
      tier_pricing_detail: [],
      applied_discount_ids: [],
    };
  }

  return {
    price_type: pricing?.priceType as PriceType,
    tier_method: pricing?.tierMethod as TierMethod,
    tier_name: pricing?.tierName ?? '',
    discount_amount: pricing?.discountAmount ?? 0,
    discount_percent: pricing?.discountPercent ?? 0,
    tier_pricing_detail:
      pricing?.tierDetails?.map(t => ({
        start_value: t.startValue ?? 0,
        price_per_value: t.pricePerValue ?? 0,
      })) ?? [],
    applied_discount_ids: (pricing as any)?.appliedDiscountIds ?? [],
  };
};

const mapLabResults = (
  labResults?: OpenSearchProductLabResult[],
): LabResult[] => {
  if (!labResults?.length) {
    return [];
  }

  return labResults?.map(l => ({
    result_type: l.type ?? '',
    amount: {
      minimum_value: l.amount?.minValue ?? '',
      maximum_value: l.amount?.maxValue ?? '',
    },
    amount_type: l.amountType ?? '',
  }));
};

const mapDiscounts = (discounts?: OpenSearchProductDiscount[]): Discount[] => {
  if (!discounts?.length) {
    return [];
  }

  return discounts?.map(d => ({
    discount_id: d.discountId ?? '',
    discount_title: d.discountTitle ?? '',
    discount_affinity: d.discountAffinity ?? '',
    discount_amount: d.discountAmount ?? 0,
    discount_method: d.discountMethod ?? '',
    discount_stackable: d.discountStackable ?? '',
  }));
};

const mapVariants = (product: OpenSearchProduct): Variant[] => {
  const p = product?.productData;
  const priceType = p?.pricing?.priceType ?? 'FLAT';

  if (priceType === 'FLAT') {
    return [mapVariant(product)];
  }

  if (priceType === 'TIER') {
    const t = p?.pricing?.tierDetails
      ?.filter(t => t?.startValue && t?.startValue > 0) // skip the first tier
      .map(t => mapTierVariant(product, t));

    return t ?? [];
  }

  return [];
};

const mapVariant = (product: OpenSearchProduct): Variant => {
  const price = calculatePrice(String(product.productData?.pricing?.priceSell));
  const salePrice = getSalePrice(
    String(product.productData?.pricing?.discountedPrice),
    price,
  );

  const discount_amount = calculatePrice(
    String(product?.productData?.pricing?.discountAmount ?? '0'),
  );
  const discount_percent = parseFloat(
    product?.productData?.pricing?.discountPercent?.toFixed(2) ?? '0',
  );

  return {
    id: product.id,
    name: product.name,
    pos_id: product.id,
    price: price,
    salePrice: salePrice,
    discount_amount,
    discount_percent,
    type: mapVariantType(product),
  };
};

const mapTierVariant = (
  product: OpenSearchProduct,
  tierPricingDetail: OpenSearchTierDetail,
): Variant => {
  // tier base price
  const price = calculatePrice(
    String(
      (tierPricingDetail.pricePerValue ?? 0) *
        (tierPricingDetail.startValue ?? 0),
    ),
  );
  const salePrice = price;

  const discount_amount = calculatePrice(
    String(product?.productData?.pricing?.discountAmount ?? '0'),
  );
  const discount_percent = parseFloat(
    product?.productData?.pricing?.discountPercent?.toFixed(2) ?? '0',
  );

  return {
    id: `${product.id}-${tierPricingDetail.startValue}`, // we are adding the weight to the id to make it unique
    name: product.name,
    pos_id: product.id,
    price: price,
    salePrice: salePrice,
    discount_amount,
    discount_percent,
    type: mapVariantType({
      ...product,
      amount: tierPricingDetail.startValue ?? 0,
    }),
  };
};

const mapVariantType = (product: OpenSearchProduct): string => {
  if (
    product.unitOfMeasurement === 'MILLIGRAMS' ||
    product.unitOfMeasurement === 'UNIT'
  ) {
    return 'each';
  }

  switch (product.amount) {
    case 0.5:
      return 'half_gram';
    case 1:
      return 'gram';
    case 2:
      return 'two_gram';
    case 3.5:
      return 'eighth_ounce';
    case 7:
      return 'quarter_ounce';
    case 14:
      return 'half_ounce';
    case 28:
      return 'ounce';
    default:
      return product.amount && product.unitOfMeasurement
        ? `${product.amount}_${product.unitOfMeasurement?.toLowerCase()}`
        : '';
  }
};
// For a BOGO or BUNDLE that has not met it’s schedule condition, the API returns discount_affinity=cart.
// This is actually a bug that should be fixed, as discount_affinity should
// be “pre-cart” for a bogo. However because this bug is consistent,
// we can build logic around it for a short-term
// fix until we add API logic to filter out inactive BOGOS and BUNDLES from the response.
const filterDiscountsOutOfSchedule = (
  discounts?: OpenSearchProductDiscount[],
): OpenSearchProductDiscount[] => {
  if (!discounts?.length) {
    return [];
  }

  const methodsToFilter = ['BOGO', 'BUNDLE'];

  return discounts
    .map(discount => {
      if (discount?.discountAffinity === 'EVAL') {
        return {
          ...discount,
          discountAffinity: isDiscountActive(discount) ? 'Pre-Cart' : 'Cart',
        };
      }
      return discount;
    })
    .filter(discount => {
      const method = discount?.discountMethod;

      const isSpecialCase = methodsToFilter.includes(method ?? '');

      if (!isSpecialCase) {
        return true;
      }

      return discount?.discountAffinity !== 'Cart';
    });
};

const isDiscountActive = (discount: OpenSearchProductDiscount): boolean => {
  if (discount?.discountConditions?.length === 1) {
    return isScheduleDiscountActive(discount?.discountConditions?.[0]);
  }

  if (discount?.discountConditions?.length === 2) {
    return (
      isScheduleDiscountActive(discount?.discountConditions?.[0]) ||
      isScheduleDiscountActive(discount?.discountConditions?.[1])
    );
  }
  return false;
};

const isScheduleDiscountActive = (
  condition: OpenSearchDiscountCondition,
): boolean => {
  if (condition?.scheduleCondition && condition?.type === 'Schedule') {
    return isScheduleActive(condition?.scheduleCondition, timezone);
  }
  return false;
};

function isScheduleActive(
  schedule: OpenSearchScheduleCondition,
  timeZone: string,
): boolean {
  const now = new Date();
  const zonedNow = toZonedTime(now, timeZone);

  if (!schedule.type || !schedule.startDate || !schedule.endDate) {
    return false;
  }

  const zonedStartDate = new Date(schedule.startDate);
  const zonedEndDate = new Date(schedule.endDate);
  // --- Logic for DO_NOT type ---
  if (schedule.type === 'DO_NOT') {
    return (
      !isBefore(zonedNow, zonedStartDate) && !isAfter(zonedNow, zonedEndDate)
    );
  }

  // --- Logic for DAY type ---
  if (schedule.type === 'DAY') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }

    if (differenceInHours(zonedEndDate, zonedStartDate) >= 24) {
      return true;
    }

    const startTime = format(zonedStartDate, 'HH:mm:ss', { timeZone });
    const endTime = format(zonedEndDate, 'HH:mm:ss', { timeZone });
    const nowTime = format(zonedNow, 'HH:mm:ss', { timeZone });

    if (endTime < startTime) {
      // Overnight
      return nowTime >= startTime || nowTime <= endTime;
    }
    // Same day
    return nowTime >= startTime && nowTime <= endTime;
  }

  // --- Logic for WEEK type ---
  if (schedule.type === 'WEEK') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetDay = getDay(zonedStartDate); // 0 = Sunday, 1 = Monday...
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    const thisWeekAnchor = setDay(zonedNow, targetDay);
    const thisWeekStart = new Date(
      thisWeekAnchor.getFullYear(),
      thisWeekAnchor.getMonth(),
      thisWeekAnchor.getDate(),
      zonedStartDate.getHours(),
      zonedStartDate.getMinutes(),
    );
    const thisWeekEnd = new Date(thisWeekStart.getTime() + duration);
    if (!isBefore(zonedNow, thisWeekStart) && !isAfter(zonedNow, thisWeekEnd))
      return true;

    const lastWeekAnchor = setDay(addWeeks(zonedNow, -1), targetDay);
    const lastWeekStart = new Date(
      lastWeekAnchor.getFullYear(),
      lastWeekAnchor.getMonth(),
      lastWeekAnchor.getDate(),
      zonedStartDate.getHours(),
      zonedStartDate.getMinutes(),
    );
    const lastWeekEnd = new Date(lastWeekStart.getTime() + duration);
    if (!isBefore(zonedNow, lastWeekStart) && !isAfter(zonedNow, lastWeekEnd))
      return true;
  }

  // --- Logic for WEEK_DAY type ---
  if (schedule.type === 'WEEK_DAY') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    let mostRecentWeekday = zonedNow;
    if (getDay(zonedNow) === 6) {
      // Saturday
      mostRecentWeekday = addDays(zonedNow, -1);
    } else if (getDay(zonedNow) === 0) {
      // Sunday
      mostRecentWeekday = addDays(zonedNow, -2);
    }

    const recurrenceStart = new Date(
      mostRecentWeekday.getFullYear(),
      mostRecentWeekday.getMonth(),
      mostRecentWeekday.getDate(),
      zonedStartDate.getHours(),
      zonedStartDate.getMinutes(),
    );
    const recurrenceEnd = new Date(recurrenceStart.getTime() + duration);

    if (
      !isBefore(zonedNow, recurrenceStart) &&
      !isAfter(zonedNow, recurrenceEnd)
    ) {
      return true;
    }
  }

  // --- Logic for MONTH type ---
  if (schedule.type === 'MONTH') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetDayOfWeek = getDay(zonedStartDate);
    const targetOrdinal = Math.floor((getDate(zonedStartDate) - 1) / 7) + 1;
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    const firstDayOfThisMonth = startOfMonth(zonedNow);
    let firstTargetDayOfThisMonth = firstDayOfThisMonth;
    while (getDay(firstTargetDayOfThisMonth) !== targetDayOfWeek) {
      firstTargetDayOfThisMonth = addDays(firstTargetDayOfThisMonth, 1);
    }
    const thisMonthAnchorDate = addDays(
      firstTargetDayOfThisMonth,
      (targetOrdinal - 1) * 7,
    );

    if (getMonth(thisMonthAnchorDate) === getMonth(zonedNow)) {
      const thisMonthStart = new Date(
        thisMonthAnchorDate.getFullYear(),
        thisMonthAnchorDate.getMonth(),
        thisMonthAnchorDate.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const thisMonthEnd = new Date(thisMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, thisMonthStart) &&
        !isAfter(zonedNow, thisMonthEnd)
      )
        return true;
    }

    const firstDayOfLastMonth = startOfMonth(addMonths(zonedNow, -1));
    let firstTargetDayOfLastMonth = firstDayOfLastMonth;
    while (getDay(firstTargetDayOfLastMonth) !== targetDayOfWeek) {
      firstTargetDayOfLastMonth = addDays(firstTargetDayOfLastMonth, 1);
    }
    const lastMonthAnchorDate = addDays(
      firstTargetDayOfLastMonth,
      (targetOrdinal - 1) * 7,
    );

    if (getMonth(lastMonthAnchorDate) === getMonth(addMonths(zonedNow, -1))) {
      const lastMonthStart = new Date(
        lastMonthAnchorDate.getFullYear(),
        lastMonthAnchorDate.getMonth(),
        lastMonthAnchorDate.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const lastMonthEnd = new Date(lastMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, lastMonthStart) &&
        !isAfter(zonedNow, lastMonthEnd)
      )
        return true;
    }
  }

  // --- Logic for MONTH_DAY type ---
  if (schedule.type === 'MONTH_DAY') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetDayOfMonth = getDate(zonedStartDate);
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    try {
      const thisMonthAnchor = setDate(zonedNow, targetDayOfMonth);
      const thisMonthStart = new Date(
        thisMonthAnchor.getFullYear(),
        thisMonthAnchor.getMonth(),
        thisMonthAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const thisMonthEnd = new Date(thisMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, thisMonthStart) &&
        !isAfter(zonedNow, thisMonthEnd)
      ) {
        return true;
      }
    } catch (e) {
      console.error('Error in MONTH_DAY schedule:', e);
    }

    try {
      const lastMonthAnchor = setDate(
        addMonths(zonedNow, -1),
        targetDayOfMonth,
      );
      const lastMonthStart = new Date(
        lastMonthAnchor.getFullYear(),
        lastMonthAnchor.getMonth(),
        lastMonthAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const lastMonthEnd = new Date(lastMonthStart.getTime() + duration);
      if (
        !isBefore(zonedNow, lastMonthStart) &&
        !isAfter(zonedNow, lastMonthEnd)
      )
        return true;
    } catch (e) {
      console.error('Error in MONTH_DAY schedule:', e);
    }
  }

  // --- Logic for ANNUAL type ---
  if (schedule.type === 'ANNUAL') {
    if (isBefore(startOfDay(zonedNow), startOfDay(zonedStartDate))) {
      return false;
    }
    const targetMonth = getMonth(zonedStartDate);
    const targetDay = getDate(zonedStartDate);
    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();

    try {
      const thisYearAnchor = setDate(
        setMonth(zonedNow, targetMonth),
        targetDay,
      );
      const thisYearStart = new Date(
        thisYearAnchor.getFullYear(),
        thisYearAnchor.getMonth(),
        thisYearAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const thisYearEnd = new Date(thisYearStart.getTime() + duration);
      if (!isBefore(zonedNow, thisYearStart) && !isAfter(zonedNow, thisYearEnd))
        return true;
    } catch (e) {
      console.error('Error in ANNUAL schedule:', e);
    }

    try {
      const lastYearAnchor = setDate(
        setMonth(addYears(zonedNow, -1), targetMonth),
        targetDay,
      );
      const lastYearStart = new Date(
        lastYearAnchor.getFullYear(),
        lastYearAnchor.getMonth(),
        lastYearAnchor.getDate(),
        zonedStartDate.getHours(),
        zonedStartDate.getMinutes(),
      );
      const lastYearEnd = new Date(lastYearStart.getTime() + duration);
      if (!isBefore(zonedNow, lastYearStart) && !isAfter(zonedNow, lastYearEnd))
        return true;
    } catch (e) {
      console.error('Error in ANNUAL schedule:', e);
    }
  }

  // --- Logic for CUSTOM type ---
  if (schedule.type === 'CUSTOM') {
    if (!schedule.repeat.intervalType || !schedule.repeat.intervalCount)
      return false;
    if (isBefore(zonedNow, zonedStartDate)) return false;
    if (
      schedule.repeat.end &&
      isAfter(
        startOfDay(zonedNow),
        startOfDay(fromZonedTime(schedule.repeat.end, timeZone)),
      )
    )
      return false;

    const duration = zonedEndDate.getTime() - zonedStartDate.getTime();
    let occurrenceCount = 0;
    let cursor = zonedStartDate;

    while (!isAfter(cursor, zonedNow)) {
      let isValidRecurrenceDay = false;

      if (schedule.repeat.intervalType.toLowerCase() === 'day') {
        const daysBetween = differenceInDays(cursor, zonedStartDate);
        if (daysBetween % schedule.repeat.intervalCount === 0)
          isValidRecurrenceDay = true;
      } else if (schedule.repeat.intervalType.toLowerCase() === 'week') {
        const weeksBetween = differenceInWeeks(
          startOfWeek(cursor),
          startOfWeek(zonedStartDate),
        );
        if (weeksBetween % schedule.repeat.intervalCount === 0) {
          const dayName = format(cursor, 'EEEE');
          if (schedule.repeat.days?.includes(dayName)) {
            isValidRecurrenceDay = true;
          }
        }
      } else if (schedule.repeat.intervalType.toLowerCase() === 'month') {
        const monthsBetween = differenceInMonths(
          startOfMonth(cursor),
          startOfMonth(zonedStartDate),
        );
        if (
          monthsBetween % schedule.repeat.intervalCount === 0 &&
          getDate(cursor) === getDate(zonedStartDate)
        ) {
          isValidRecurrenceDay = true;
        }
      } else if (schedule.repeat.intervalType.toLowerCase() === 'year') {
        const yearsBetween = differenceInYears(
          startOfYear(cursor),
          startOfYear(zonedStartDate),
        );
        if (
          yearsBetween % schedule.repeat.intervalCount === 0 &&
          getMonth(cursor) === getMonth(zonedStartDate) &&
          getDate(cursor) === getDate(zonedStartDate)
        ) {
          isValidRecurrenceDay = true;
        }
      }

      if (isValidRecurrenceDay) {
        occurrenceCount++;
        const eventStart = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate(),
          zonedStartDate.getHours(),
          zonedStartDate.getMinutes(),
        );
        const eventEnd = new Date(eventStart.getTime() + duration);
        if (!isBefore(zonedNow, eventStart) && !isAfter(zonedNow, eventEnd)) {
          if (
            schedule.repeat.occurrences &&
            occurrenceCount > schedule.repeat.occurrences
          ) {
            return false;
          }
          return true;
        }
      }
      cursor = addDays(cursor, 1);
    }
  }

  return false;
}

const priceType = (product: OpenSearchProduct): string => {
  const type = product?.productData?.pricing?.priceType ?? 'FLAT';

  if (
    type === 'TIER' &&
    product.subtype &&
    [
      'BULK FLOWER',
      'SHAKE',
      'STRAIN SPECIFIC SHAKE',
      'PRE-PACK',
      'BULK EXTRACT',
    ].includes(product.subtype)
  ) {
    return 'TIER';
  }

  return 'FLAT';
};

const mapPhotoAssets = (product: OpenSearchProduct): Asset[] => {
  if (product?.productData?.images?.length) {
    return product.productData.images.map(image => ({ image }));
  }

  return [generateOpenSearchStockImage(product)];
};

const generateOpenSearchStockImage = (product: OpenSearchProduct) => {
  const productType = (data as unknown as IStoreMetadata)?.productCard;

  if (productType) {
    const stockImage = getStockItem(
      productType?.stock_image,
      product.category,
      product.subtype,
    );

    if (stockImage) {
      return {
        image: stockImage?.default_product_image?.url,
        stock: true,
      };
    }
  }

  return {
    image: PLACEHOLDER,
    stock: true,
  };
};

const getProductsFromOpenSearchResponse = (
  result: ResponseBody,
): GProduct[] => {
  const products = (result?.hits?.hits ?? []).map(
    ({ _source }) => mapProduct(_source as OpenSearchProduct) as GProduct,
  );

  return products;
};

export { mapProduct, getProductsFromOpenSearchResponse };
